import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AssetStatus } from "@/v2/shared/model";
import {
  acceptRequest,
  addDocument,
  addDossierComment,
  addRequestComment,
  archiveRequest,
  closeRequest,
  completeDeployment,
  createRequest,
  extendDossier,
  importAssetsFromExcel,
  markDeployed,
  planDeployment,
  removeDeployment,
  replaceDeploymentAsset,
  setAssetStatus,
  setTaskReminder,
  toggleTask,
  updateDossierPeriod,
  updateRequest,
  type CreateRequestInput,
  type PlanDeploymentInput,
  type TaskTarget,
  type UpdateRequestInput,
} from "@/v2/data/db";
import { v2Keys } from "@/v2/data/keys";

function useInvalidateRequests(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: v2Keys.requests.all });
}

function useInvalidateAssets(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: v2Keys.assets.all });
}

export function useCreateRequest() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (input: CreateRequestInput) => createRequest(input),
    onSuccess: invalidate,
  });
}

// CB-10/CB-15: opmerking plaatsen, optioneel met mail naar de aanvrager.
export function useAddComment() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      text: string;
      author: string;
      sendMail: boolean;
      handlerId?: string;
    }) => addRequestComment(vars.id, vars.text, vars.author, vars.sendMail, vars.handlerId),
    onSuccess: invalidate,
  });
}

// CB-15: interne opmerking op het dossier (enkel zichtbaar voor beheerders).
export function useAddDossierComment() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      requestId: string;
      dossierId: string;
      text: string;
      author: string;
    }) => addDossierComment(vars.requestId, vars.dossierId, vars.text, vars.author),
    onSuccess: invalidate,
  });
}

// CB-1: aanvraag corrigeren door de beheerder.
export function useUpdateRequest() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateRequestInput; author: string }) =>
      updateRequest(vars.id, vars.input, vars.author),
    onSuccess: invalidate,
  });
}

// CB-12: aanvaarden + dossier aanmaken (op wachtrij, zonder asset).
export function useAcceptRequest() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      startDate: string;
      endDate: string;
      author: string;
      handlerId?: string;
    }) => acceptRequest(vars.id, vars.startDate, vars.endDate, vars.author, vars.handlerId),
    onSuccess: invalidate,
  });
}

export function usePlanDeployment() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      requestId: string;
      dossierId: string;
      input: PlanDeploymentInput;
      author: string;
    }) => planDeployment(vars.requestId, vars.dossierId, vars.input, vars.author),
    onSuccess: invalidate,
  });
}

// CB-22: verlenging.
export function useExtendDossier() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: { requestId: string; dossierId: string; author: string }) =>
      extendDossier(vars.requestId, vars.dossierId, vars.author),
    onSuccess: invalidate,
  });
}

// Toegekende periode vrij aanpassen (enkel zolang het dossier niet lopend is).
export function useUpdateDossierPeriod() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      requestId: string;
      dossierId: string;
      startDate: string;
      endDate: string;
      author: string;
    }) =>
      updateDossierPeriod(vars.requestId, vars.dossierId, vars.startDate, vars.endDate, vars.author),
    onSuccess: invalidate,
  });
}

export function useRemoveDeployment() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      requestId: string;
      dossierId: string;
      deploymentId: string;
      author: string;
    }) => removeDeployment(vars.requestId, vars.dossierId, vars.deploymentId, vars.author),
    onSuccess: invalidate,
  });
}

export function useReplaceAsset() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      requestId: string;
      dossierId: string;
      deploymentId: string;
      newAssetId: string;
      reason: string;
      author: string;
    }) =>
      replaceDeploymentAsset(
        vars.requestId,
        vars.dossierId,
        vars.deploymentId,
        vars.newAssetId,
        vars.reason,
        vars.author,
      ),
    onSuccess: invalidate,
  });
}

// CB-39: plaatsing registreren (Geplaatst).
export function useMarkDeployed() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      requestId: string;
      dossierId: string;
      deploymentId: string;
      actualStart: string;
      executedBy: string;
      author: string;
    }) =>
      markDeployed(
        vars.requestId,
        vars.dossierId,
        vars.deploymentId,
        vars.actualStart,
        vars.executedBy,
        vars.author,
      ),
    onSuccess: invalidate,
  });
}

// CB-39: ophaling registreren (Afgerond).
export function useCompleteDeployment() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      requestId: string;
      dossierId: string;
      deploymentId: string;
      actualEnd: string;
      author: string;
    }) =>
      completeDeployment(
        vars.requestId,
        vars.dossierId,
        vars.deploymentId,
        vars.actualEnd,
        vars.author,
      ),
    onSuccess: invalidate,
  });
}

export function useCloseRequest() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: { id: string; author: string }) => closeRequest(vars.id, vars.author),
    onSuccess: invalidate,
  });
}

export function useArchiveRequest() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: { id: string; author: string }) => archiveRequest(vars.id, vars.author),
    onSuccess: invalidate,
  });
}

// CB-17: taak afvinken.
export function useToggleTask() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      requestId: string;
      target: TaskTarget;
      taskId: string;
      author: string;
    }) => toggleTask(vars.requestId, vars.target, vars.taskId, vars.author),
    onSuccess: invalidate,
  });
}

// CB-17: optionele herinnering (datum + eventueel tijdstip) op een taak,
// optioneel gericht aan één beheerder.
export function useSetTaskReminder() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      requestId: string;
      target: TaskTarget;
      taskId: string;
      reminderDate?: string;
      reminderTime?: string;
      reminderAssigneeId?: string;
      author: string;
    }) =>
      setTaskReminder(
        vars.requestId,
        vars.target,
        vars.taskId,
        vars.reminderDate,
        vars.reminderTime,
        vars.reminderAssigneeId,
        vars.author,
      ),
    onSuccess: invalidate,
  });
}

export function useAddDocument() {
  const invalidate = useInvalidateRequests();
  return useMutation({
    mutationFn: (vars: {
      requestId: string;
      filename: string;
      sizeLabel: string;
      author: string;
    }) => addDocument(vars.requestId, vars.filename, vars.sizeLabel, vars.author),
    onSuccess: invalidate,
  });
}

export function useSetAssetStatus() {
  const invalidate = useInvalidateAssets();
  return useMutation({
    mutationFn: (vars: { assetId: string; status: AssetStatus }) =>
      setAssetStatus(vars.assetId, vars.status),
    onSuccess: invalidate,
  });
}

// CB-29: gemockte Excel-import (bijwerkend).
export function useImportAssets() {
  const invalidate = useInvalidateAssets();
  return useMutation({
    mutationFn: () => importAssetsFromExcel(),
    onSuccess: invalidate,
  });
}
